/**
 * CandidateOutreachPipeline
 *
 * The admin `/admin/jobs/:jobId` candidate pipeline + outreach email
 * management + reply management + bulk operations + auto-followup
 * surface (GitHub issue #498 — "E2E: Sourcing & outreach — pipeline
 * tracking, email management, replies").
 *
 * Renders inside `app/routes/admin/JobDetail.tsx`'s "Pipeline" tab,
 * replacing that file's previous separate `candidates`/`outreachMessages`
 * mock-`useEffect` blocks (see `@/lib/api/apiClient.ts`'s
 * `useAdminJobPipeline` doc-comment for why: this issue's thread/bulk/
 * reply/auto-followup surface needs a single richer shape those two
 * TODO'd endpoints never covered).
 *
 * Data flow: `useAdminJobPipeline(jobId)` seeds local component state
 * (`entries`), which every action below mutates optimistically — the
 * network call each action fires (via the mutation hooks in
 * `@/lib/api/apiClient.ts`) is best-effort against a backend that does not
 * exist yet (see that file's module doc-comment); the UI does not block
 * or roll back on a failed/mocked request, so this component is fully
 * exercisable by Playwright specs stubbing those paths with
 * `page.route()`.
 *
 * `data-testid` attributes throughout are the contract
 * `e2e/sourcing-outreach.spec.ts` drives/asserts against.
 */

import { useEffect, useState } from 'react';

import {
  useAdminJobPipeline,
  useEditOutreachEmail,
  useRegenerateOutreachEmail,
  useSendOutreachEmail,
  useSendAllQueuedOutreach,
  useSyncReplies,
  useComposeReply,
  useAiGenerateReply,
  useBulkStatusChange,
  useBulkSendOutreach,
  useBulkRegenerateOutreach,
  useToggleCandidateAutoFollowup,
  useToggleJobAutoFollowup,
  reportApiError,
} from '@/lib/api/apiClient';
import type { PipelineCandidate, OutreachMessageStatus } from '@/lib/validation/outreachPipeline';
import { Skeleton } from '@/components/ui/skeleton';

type PipelineTab = 'ALL' | 'QUEUED' | 'SENT' | 'REPLIED' | 'BOUNCED';

const TABS: { key: PipelineTab; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'QUEUED', label: 'Queued' },
  { key: 'SENT', label: 'Sent' },
  { key: 'REPLIED', label: 'Replied' },
  { key: 'BOUNCED', label: 'Bounced' },
];

/**
 * "Has deliverable email" filter (GitHub issue "[Email] Candidate email
 * reachability — multi-provider enrichment (root cause of 'screened lots,
 * queued 0')") — mirrors the backend's placeholder-detection predicate
 * (`apps/modal/src/sourcing_pipeline.py`'s `_looks_like_placeholder_email`
 * / `prescreen_filters.is_reachable_email`): a candidate's email is
 * "deliverable" only if it's present and isn't one of Stage 2's
 * `@linkedin.com`/`unknown+...`-shaped placeholders. Lets a recruiter see
 * the reachable-candidate rate directly in this pipeline view (this
 * issue's demo step: "filter by 'has deliverable email'" -> a count/
 * percentage higher than the pre-enrichment 38% baseline), without
 * waiting on a dedicated dashboard.
 */
function hasDeliverableEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  const normalized = email.trim().toLowerCase();
  return normalized.length > 0 && !normalized.endsWith('@linkedin.com') && !normalized.startsWith('unknown+');
}

// TODO(backend, out of scope for this ticket): this component's data
// source (`useAdminJobPipeline` -> `GET /admin/jobs/:jobId/pipeline`) has
// no real backend route yet (per docs/flows/GATE-113-SIGNOFF.md's
// "sourcing & outreach" ⚠️ finding — confirmed here: no matching route in
// apps/workers/src/api/** or functions/api/**), so this filter/rate is
// only exercisable today via `page.route()`-mocked Playwright fixtures,
// not a live job. Once that endpoint is real, this same
// `deliverableEmailCount`/`deliverableEmailPct` computation is what
// should back this issue's demo step ("Open the job's candidate list and
// filter by 'has deliverable email'" -> a rate above the pre-enrichment
// 38% baseline) end to end.

function matchesTab(status: OutreachMessageStatus, tab: PipelineTab): boolean {
  if (tab === 'ALL') return true;
  if (tab === 'QUEUED') return status === 'DRAFT' || status === 'QUEUED';
  if (tab === 'SENT') return status === 'SENT' || status === 'OPENED';
  if (tab === 'REPLIED') return status === 'REPLIED';
  if (tab === 'BOUNCED') return status === 'BOUNCED' || status === 'FAILED';
  return true;
}

function statusBadgeClasses(status: OutreachMessageStatus): string {
  switch (status) {
    case 'SENT':
    case 'OPENED':
      return 'bg-blue-100 text-blue-800';
    case 'REPLIED':
      return 'bg-green-100 text-green-800';
    case 'BOUNCED':
    case 'FAILED':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

/** Minimal, dependency-free modal (no Radix `Dialog` is installed in
 * `apps/web` — see `package.json` — so this mirrors the plain-div
 * overlay pattern already used for loading/error states elsewhere in
 * `app/routes/admin/*.tsx`, rather than adding a new dependency). */
function Modal({
  testId,
  title,
  children,
  onClose,
}: {
  testId: string;
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-testid={testId}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg border border-border bg-background p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-4 text-lg font-semibold">{title}</h3>
        {children}
      </div>
    </div>
  );
}

interface PendingConfirm {
  kind: 'send-one' | 'send-all' | 'bulk-send';
  candidateId?: string;
}

export default function CandidateOutreachPipeline({ jobId }: { jobId: string }) {
  const { data, isLoading, isError, error } = useAdminJobPipeline(jobId);

  const [entries, setEntries] = useState<PipelineCandidate[]>([]);
  const [jobAutoFollowup, setJobAutoFollowup] = useState(true);
  const [activeTab, setActiveTab] = useState<PipelineTab>('ALL');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [openThreadId, setOpenThreadId] = useState<string | null>(null);
  const [editingBody, setEditingBody] = useState<string>('');
  const [isEditingBody, setIsEditingBody] = useState(false);
  const [regenInstructions, setRegenInstructions] = useState('');
  const [replyDraft, setReplyDraft] = useState('');
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null);
  const [bulkStatusValue, setBulkStatusValue] = useState<OutreachMessageStatus>('SENT');
  const [deliverableEmailOnly, setDeliverableEmailOnly] = useState(false);
  const [showBulkRegenModal, setShowBulkRegenModal] = useState(false);
  const [bulkRegenInstructions, setBulkRegenInstructions] = useState('');

  useEffect(() => {
    if (isError) reportApiError(error, 'Failed to load candidate pipeline');
  }, [isError, error]);

  useEffect(() => {
    if (data) {
      setEntries(data.candidates);
      setJobAutoFollowup(data.autoFollowupEnabled);
    }
  }, [data]);

  const editEmail = useEditOutreachEmail();
  const regenerateEmail = useRegenerateOutreachEmail();
  const sendEmail = useSendOutreachEmail();
  const sendAllQueued = useSendAllQueuedOutreach();
  const syncReplies = useSyncReplies();
  const composeReply = useComposeReply();
  const aiGenerateReply = useAiGenerateReply();
  const bulkStatusChange = useBulkStatusChange();
  const bulkSend = useBulkSendOutreach();
  const bulkRegenerate = useBulkRegenerateOutreach();
  const toggleCandidateAutoFollowup = useToggleCandidateAutoFollowup();
  const toggleJobAutoFollowup = useToggleJobAutoFollowup();

  if (isLoading) {
    return (
      <div className="space-y-4" aria-busy="true" data-testid="pipeline-loading">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="py-8 text-center text-muted-foreground" data-testid="pipeline-error">
        Failed to load the candidate pipeline.
      </div>
    );
  }

  const openEntry = entries.find((e) => e.candidate.uuid === openThreadId) ?? null;

  const counts = {
    ALL: entries.length,
    QUEUED: entries.filter((e) => matchesTab(e.message.status, 'QUEUED')).length,
    SENT: entries.filter((e) => matchesTab(e.message.status, 'SENT')).length,
    REPLIED: entries.filter((e) => matchesTab(e.message.status, 'REPLIED')).length,
    BOUNCED: entries.filter((e) => matchesTab(e.message.status, 'BOUNCED')).length,
  };

  const visibleEntries = entries
    .filter((e) => matchesTab(e.message.status, activeTab))
    .filter((e) => !deliverableEmailOnly || hasDeliverableEmail(e.candidate.email));
  const queuedEntries = entries.filter((e) => matchesTab(e.message.status, 'QUEUED'));

  // This issue's demo step: "filter by 'has deliverable email'" -> a
  // count/percentage higher than the pre-enrichment 38% baseline. Scoped
  // to the pipeline's full candidate set (not just the current tab) so
  // the percentage is a stable, tab-independent reachability measure.
  const deliverableEmailCount = entries.filter((e) => hasDeliverableEmail(e.candidate.email)).length;
  const deliverableEmailPct =
    entries.length > 0 ? Math.round((deliverableEmailCount / entries.length) * 100) : 0;

  function updateEntry(
    candidateUuid: string,
    updater: (e: PipelineCandidate) => PipelineCandidate
  ) {
    setEntries((prev) => prev.map((e) => (e.candidate.uuid === candidateUuid ? updater(e) : e)));
  }

  function toggleSelected(uuid: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  }

  function openThread(candidate: PipelineCandidate) {
    setOpenThreadId(candidate.candidate.uuid);
    setIsEditingBody(false);
    setEditingBody(candidate.message.body);
    setRegenInstructions('');
    setReplyDraft('');
  }

  function closeThread() {
    setOpenThreadId(null);
  }

  function saveEditedBody() {
    if (!openEntry) return;
    const messageId = openEntry.message.id;
    updateEntry(openEntry.candidate.uuid, (e) => ({
      ...e,
      message: { ...e.message, body: editingBody },
    }));
    setIsEditingBody(false);
    editEmail.mutate({ messageId, body: editingBody });
  }

  function regenerate() {
    if (!openEntry) return;
    const messageId = openEntry.message.id;
    const instructions = regenInstructions;
    const newBody = `${openEntry.message.body}\n\n[Regenerated${instructions ? ` per instructions: "${instructions}"` : ''}]`;
    updateEntry(openEntry.candidate.uuid, (e) => ({
      ...e,
      message: { ...e.message, body: newBody },
    }));
    setEditingBody(newBody);
    regenerateEmail.mutate({ messageId, instructions });
  }

  function confirmSendOne() {
    if (!pendingConfirm || pendingConfirm.kind !== 'send-one' || !pendingConfirm.candidateId)
      return;
    const candidateId = pendingConfirm.candidateId;
    const entry = entries.find((e) => e.candidate.uuid === candidateId);
    if (!entry) {
      setPendingConfirm(null);
      return;
    }
    const sentAt = new Date().toISOString();
    updateEntry(candidateId, (e) => ({
      ...e,
      message: { ...e.message, status: 'SENT', sent_at: sentAt },
      thread: [
        ...e.thread,
        {
          id: `local-sent-${Date.now()}`,
          direction: 'OUTBOUND',
          author: 'ADMIN',
          subject: e.message.subject,
          body: e.message.body,
          aiGenerated: false,
          createdAt: sentAt,
        },
      ],
    }));
    sendEmail.mutate({ messageId: entry.message.id });
    setPendingConfirm(null);
  }

  function confirmSendAll() {
    const messageIds = queuedEntries.map((e) => e.message.id);
    const sentAt = new Date().toISOString();
    setEntries((prev) =>
      prev.map((e) =>
        matchesTab(e.message.status, 'QUEUED')
          ? { ...e, message: { ...e.message, status: 'SENT', sent_at: sentAt } }
          : e
      )
    );
    sendAllQueued.mutate({ jobId, messageIds });
    setPendingConfirm(null);
  }

  function confirmBulkSend() {
    const targets = entries.filter(
      (e) => selectedIds.has(e.candidate.uuid) && matchesTab(e.message.status, 'QUEUED')
    );
    const messageIds = targets.map((e) => e.message.id);
    const sentAt = new Date().toISOString();
    setEntries((prev) =>
      prev.map((e) =>
        targets.some((t) => t.candidate.uuid === e.candidate.uuid)
          ? { ...e, message: { ...e.message, status: 'SENT', sent_at: sentAt } }
          : e
      )
    );
    bulkSend.mutate({ messageIds });
    setPendingConfirm(null);
    setSelectedIds(new Set());
  }

  function applyBulkStatusChange() {
    const candidateIds = Array.from(selectedIds);
    setEntries((prev) =>
      prev.map((e) =>
        candidateIds.includes(e.candidate.uuid)
          ? { ...e, message: { ...e.message, status: bulkStatusValue } }
          : e
      )
    );
    bulkStatusChange.mutate({ candidateIds, status: bulkStatusValue });
    setSelectedIds(new Set());
  }

  function applyBulkRegenerate() {
    const targets = entries.filter((e) => selectedIds.has(e.candidate.uuid));
    const messageIds = targets.map((e) => e.message.id);
    setEntries((prev) =>
      prev.map((e) =>
        targets.some((t) => t.candidate.uuid === e.candidate.uuid)
          ? {
              ...e,
              message: {
                ...e.message,
                body: `${e.message.body}\n\n[Bulk regenerated${bulkRegenInstructions ? `: "${bulkRegenInstructions}"` : ''}]`,
              },
            }
          : e
      )
    );
    bulkRegenerate.mutate({ messageIds, instructions: bulkRegenInstructions });
    setShowBulkRegenModal(false);
    setBulkRegenInstructions('');
    setSelectedIds(new Set());
  }

  function handleSyncReplies() {
    if (!jobId) return;
    syncReplies.mutate(
      { jobId },
      {
        onSuccess: (result) => {
          if (!result.newReplies.length) return;
          setEntries((prev) =>
            prev.map((e) => {
              const additions = result.newReplies.filter((r) => r.messageId === e.message.id);
              if (!additions.length) return e;
              return {
                ...e,
                message: { ...e.message, status: 'REPLIED' },
                thread: [...e.thread, ...additions.map((a) => a.reply)],
              };
            })
          );
        },
      }
    );
  }

  function sendComposedReply() {
    if (!openEntry || !replyDraft.trim()) return;
    const messageId = openEntry.message.id;
    const body = replyDraft;
    const now = new Date().toISOString();
    updateEntry(openEntry.candidate.uuid, (e) => ({
      ...e,
      thread: [
        ...e.thread,
        {
          id: `local-reply-${Date.now()}`,
          direction: 'OUTBOUND',
          author: 'ADMIN',
          subject: null,
          body,
          aiGenerated: false,
          createdAt: now,
        },
      ],
    }));
    setReplyDraft('');
    composeReply.mutate({ messageId, body });
  }

  function aiGenerateDraft() {
    if (!openEntry) return;
    aiGenerateReply.mutate(
      { messageId: openEntry.message.id },
      {
        onSuccess: (draft) => setReplyDraft(draft.body),
        onError: () =>
          setReplyDraft(
            `Thanks for reaching out! I'd love to hear more about the ${openEntry.candidate.title ?? 'role'} opportunity. When works for a quick call?`
          ),
      }
    );
  }

  function toggleCandidateFollowup(entry: PipelineCandidate) {
    const optOut = !entry.autoFollowupOptOut;
    updateEntry(entry.candidate.uuid, (e) => ({ ...e, autoFollowupOptOut: optOut }));
    toggleCandidateAutoFollowup.mutate({ candidateId: entry.candidate.uuid, optOut });
  }

  function toggleJobFollowup() {
    const enabled = !jobAutoFollowup;
    setJobAutoFollowup(enabled);
    toggleJobAutoFollowup.mutate({ jobId, enabled });
  }

  return (
    <div data-testid="candidate-pipeline">
      {/* Toolbar */}
      <div
        className="mb-4 flex flex-wrap items-center justify-between gap-3"
        data-testid="pipeline-toolbar"
      >
        <div className="flex gap-2 border-b border-border" data-testid="pipeline-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              data-testid={`tab-${tab.key.toLowerCase()}`}
              onClick={() => setActiveTab(tab.key)}
              className={`px-3 py-2 text-sm font-medium border-b-2 ${
                activeTab === tab.key
                  ? 'border-foreground'
                  : 'border-transparent text-muted-foreground'
              }`}
            >
              {tab.label} ({counts[tab.key]})
            </button>
          ))}
        </div>

        <div className="flex items-center gap-4">
          <label
            className="flex items-center gap-2 text-sm"
            data-testid="deliverable-email-filter-toggle"
          >
            <input
              type="checkbox"
              checked={deliverableEmailOnly}
              onChange={() => setDeliverableEmailOnly((prev) => !prev)}
              aria-label="Has deliverable email"
            />
            Has deliverable email
          </label>
          <span className="text-sm text-muted-foreground" data-testid="deliverable-email-rate">
            {deliverableEmailCount} of {entries.length} ({deliverableEmailPct}%) have a deliverable
            email
          </span>

          <label className="flex items-center gap-2 text-sm" data-testid="job-auto-followup-toggle">
            <input
              type="checkbox"
              checked={jobAutoFollowup}
              onChange={toggleJobFollowup}
              aria-label="Job-level auto-followup"
            />
            Auto-followup for this job
          </label>

          <button
            data-testid="sync-replies-button"
            onClick={handleSyncReplies}
            disabled={syncReplies.isPending}
            className="px-3 py-2 text-sm font-medium border border-border rounded-md hover:bg-accent disabled:opacity-50"
          >
            {syncReplies.isPending ? 'Syncing…' : 'Sync Replies'}
          </button>

          <button
            data-testid="send-all-queued-button"
            onClick={() => setPendingConfirm({ kind: 'send-all' })}
            disabled={queuedEntries.length === 0}
            className="px-3 py-2 text-sm font-medium bg-foreground text-background rounded-md disabled:opacity-50"
          >
            Send All Queued ({queuedEntries.length})
          </button>
        </div>
      </div>

      {/* Bulk actions bar */}
      {selectedIds.size > 0 && (
        <div
          className="mb-4 flex flex-wrap items-center gap-3 rounded-md border border-border bg-muted/40 p-3"
          data-testid="bulk-actions-bar"
        >
          <span className="text-sm font-medium" data-testid="bulk-selected-count">
            {selectedIds.size} selected
          </span>

          <select
            value={bulkStatusValue}
            onChange={(e) => setBulkStatusValue(e.target.value as OutreachMessageStatus)}
            className="px-2 py-1 text-sm border border-border rounded-md"
            data-testid="bulk-status-select"
          >
            <option value="QUEUED">Queued</option>
            <option value="SENT">Sent</option>
            <option value="REPLIED">Replied</option>
            <option value="BOUNCED">Bounced</option>
          </select>
          <button
            onClick={applyBulkStatusChange}
            className="px-3 py-1 text-sm font-medium border border-border rounded-md hover:bg-accent"
            data-testid="bulk-status-apply-button"
          >
            Change Status
          </button>

          <button
            onClick={() => setPendingConfirm({ kind: 'bulk-send' })}
            className="px-3 py-1 text-sm font-medium bg-foreground text-background rounded-md"
            data-testid="bulk-send-button"
          >
            Bulk Send
          </button>

          <button
            onClick={() => setShowBulkRegenModal(true)}
            className="px-3 py-1 text-sm font-medium border border-border rounded-md hover:bg-accent"
            data-testid="bulk-regenerate-button"
          >
            Bulk Regenerate
          </button>
        </div>
      )}

      {/* Candidate cards */}
      {visibleEntries.length === 0 ? (
        <div className="py-8 text-center text-muted-foreground" data-testid="pipeline-empty">
          No candidates in this stage.
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2" data-testid="candidate-card-list">
          {visibleEntries.map((entry) => (
            <div
              key={entry.candidate.uuid}
              className="rounded-lg border border-border p-4 hover:shadow-md transition-shadow cursor-pointer"
              data-testid={`candidate-card-${entry.candidate.uuid}`}
              onClick={() => openThread(entry)}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(entry.candidate.uuid)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleSelected(entry.candidate.uuid)}
                    data-testid={`candidate-checkbox-${entry.candidate.uuid}`}
                    aria-label={`Select ${entry.candidate.name}`}
                  />
                  <div>
                    <p className="font-semibold" data-testid="candidate-name">
                      {entry.candidate.name}
                    </p>
                    <p className="text-xs text-muted-foreground" data-testid="candidate-title">
                      {entry.candidate.title ?? '—'}
                    </p>
                    <p
                      className="text-xs font-mono text-muted-foreground"
                      data-testid="candidate-email"
                    >
                      {entry.candidate.email ?? '—'}
                    </p>
                  </div>
                </div>
                <span
                  className={`px-2 py-1 text-xs font-semibold rounded-full ${statusBadgeClasses(entry.message.status)}`}
                  data-testid="candidate-status-badge"
                >
                  {entry.message.status}
                </span>
              </div>
              <div className="mt-2 text-xs text-muted-foreground" data-testid="candidate-score">
                Fit score:{' '}
                {entry.candidate.fit_score != null
                  ? `${Math.round(entry.candidate.fit_score * 100)}%`
                  : '—'}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Thread panel */}
      {openEntry && (
        <div
          className="fixed inset-y-0 right-0 z-40 w-full max-w-md overflow-y-auto border-l border-border bg-background p-6 shadow-xl"
          data-testid="thread-panel"
        >
          <div className="mb-4 flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold" data-testid="thread-panel-candidate-name">
                {openEntry.candidate.name}
              </h3>
              <p className="text-sm text-muted-foreground">{openEntry.candidate.title ?? '—'}</p>
              <p className="text-xs font-mono text-muted-foreground">
                {openEntry.candidate.email ?? '—'}
              </p>
              <p className="text-xs text-muted-foreground">
                Fit score:{' '}
                {openEntry.candidate.fit_score != null
                  ? `${Math.round(openEntry.candidate.fit_score * 100)}%`
                  : '—'}
              </p>
            </div>
            <button
              onClick={closeThread}
              data-testid="thread-panel-close"
              className="px-2 py-1 text-sm border border-border rounded-md hover:bg-accent"
            >
              Close
            </button>
          </div>

          <label
            className="mb-4 flex items-center gap-2 text-sm"
            data-testid="candidate-auto-followup-toggle"
          >
            <input
              type="checkbox"
              checked={!openEntry.autoFollowupOptOut}
              onChange={() => toggleCandidateFollowup(openEntry)}
              aria-label="Candidate auto-followup"
            />
            Auto-followup enabled for this candidate
          </label>

          <section className="mb-6 rounded-md border border-border p-3">
            <p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">
              Outreach Email ({openEntry.message.status})
            </p>
            <p className="mb-2 text-sm font-medium" data-testid="thread-email-subject">
              {openEntry.message.subject}
            </p>

            {isEditingBody ? (
              <>
                <textarea
                  value={editingBody}
                  onChange={(e) => setEditingBody(e.target.value)}
                  rows={6}
                  className="mb-2 w-full rounded-md border border-border p-2 text-sm"
                  data-testid="email-body-textarea"
                />
                <div className="flex gap-2">
                  <button
                    onClick={saveEditedBody}
                    data-testid="save-email-button"
                    className="px-3 py-1 text-sm font-medium bg-foreground text-background rounded-md"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setIsEditingBody(false)}
                    className="px-3 py-1 text-sm font-medium border border-border rounded-md hover:bg-accent"
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="mb-2 whitespace-pre-wrap text-sm" data-testid="thread-email-body">
                  {openEntry.message.body}
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setIsEditingBody(true)}
                    data-testid="edit-email-button"
                    className="px-3 py-1 text-sm font-medium border border-border rounded-md hover:bg-accent"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() =>
                      setPendingConfirm({ kind: 'send-one', candidateId: openEntry.candidate.uuid })
                    }
                    disabled={
                      openEntry.message.status !== 'DRAFT' && openEntry.message.status !== 'QUEUED'
                    }
                    data-testid="send-email-button"
                    className="px-3 py-1 text-sm font-medium bg-foreground text-background rounded-md disabled:opacity-50"
                  >
                    Send
                  </button>
                </div>
              </>
            )}

            <div className="mt-3 border-t border-border pt-3">
              <textarea
                value={regenInstructions}
                onChange={(e) => setRegenInstructions(e.target.value)}
                placeholder="Instructions for regenerating this email (e.g. 'make it shorter')"
                rows={2}
                className="mb-2 w-full rounded-md border border-border p-2 text-sm"
                data-testid="regenerate-instructions-input"
              />
              <button
                onClick={regenerate}
                data-testid="regenerate-email-button"
                className="px-3 py-1 text-sm font-medium border border-border rounded-md hover:bg-accent"
              >
                Regenerate with AI
              </button>
            </div>
          </section>

          <section className="mb-6">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase text-muted-foreground">Thread</p>
            </div>
            <div className="space-y-2" data-testid="thread-messages">
              {openEntry.thread.map((msg) => (
                <div
                  key={msg.id}
                  className={`rounded-md border border-border p-2 text-sm ${
                    msg.author === 'CANDIDATE' ? 'bg-muted/40' : ''
                  }`}
                  data-testid={`thread-message-${msg.id}`}
                >
                  <p className="mb-1 text-xs font-semibold text-muted-foreground">
                    {msg.author === 'CANDIDATE' ? openEntry.candidate.name : 'You'}
                    {msg.aiGenerated ? ' (AI-drafted)' : ''} ·{' '}
                    {new Date(msg.createdAt).toLocaleString()}
                  </p>
                  <p className="whitespace-pre-wrap">{msg.body}</p>
                </div>
              ))}
              {openEntry.thread.length === 0 && (
                <p className="text-sm text-muted-foreground">No messages yet.</p>
              )}
            </div>
          </section>

          <section>
            <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Reply</p>
            <textarea
              value={replyDraft}
              onChange={(e) => setReplyDraft(e.target.value)}
              rows={3}
              placeholder="Write a reply…"
              className="mb-2 w-full rounded-md border border-border p-2 text-sm"
              data-testid="reply-input"
            />
            <div className="flex gap-2">
              <button
                onClick={sendComposedReply}
                disabled={!replyDraft.trim()}
                data-testid="send-reply-button"
                className="px-3 py-1 text-sm font-medium bg-foreground text-background rounded-md disabled:opacity-50"
              >
                Send Reply
              </button>
              <button
                onClick={aiGenerateDraft}
                data-testid="ai-generate-reply-button"
                className="px-3 py-1 text-sm font-medium border border-border rounded-md hover:bg-accent"
              >
                AI Generate
              </button>
            </div>
          </section>
        </div>
      )}

      {/* Confirm modals */}
      {pendingConfirm?.kind === 'send-one' && (
        <Modal
          testId="send-confirm-modal"
          title="Send this email?"
          onClose={() => setPendingConfirm(null)}
        >
          <p className="mb-4 text-sm text-muted-foreground">
            This will send the outreach email to {openEntry?.candidate.name ?? 'this candidate'}.
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setPendingConfirm(null)}
              data-testid="send-confirm-cancel"
              className="px-3 py-2 text-sm font-medium border border-border rounded-md hover:bg-accent"
            >
              Cancel
            </button>
            <button
              onClick={confirmSendOne}
              data-testid="send-confirm-confirm"
              className="px-3 py-2 text-sm font-medium bg-foreground text-background rounded-md"
            >
              Send Email
            </button>
          </div>
        </Modal>
      )}

      {pendingConfirm?.kind === 'send-all' && (
        <Modal
          testId="send-all-confirm-modal"
          title={`Send all ${queuedEntries.length} queued emails?`}
          onClose={() => setPendingConfirm(null)}
        >
          <p className="mb-4 text-sm text-muted-foreground">
            This will send outreach emails to all {queuedEntries.length} queued candidates.
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setPendingConfirm(null)}
              data-testid="send-all-confirm-cancel"
              className="px-3 py-2 text-sm font-medium border border-border rounded-md hover:bg-accent"
            >
              Cancel
            </button>
            <button
              onClick={confirmSendAll}
              data-testid="send-all-confirm-confirm"
              className="px-3 py-2 text-sm font-medium bg-foreground text-background rounded-md"
            >
              Send All
            </button>
          </div>
        </Modal>
      )}

      {pendingConfirm?.kind === 'bulk-send' && (
        <Modal
          testId="bulk-send-confirm-modal"
          title={`Send emails to ${selectedIds.size} selected candidates?`}
          onClose={() => setPendingConfirm(null)}
        >
          <p className="mb-4 text-sm text-muted-foreground" data-testid="bulk-send-confirm-count">
            {selectedIds.size} candidates selected.
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setPendingConfirm(null)}
              data-testid="bulk-send-confirm-cancel"
              className="px-3 py-2 text-sm font-medium border border-border rounded-md hover:bg-accent"
            >
              Cancel
            </button>
            <button
              onClick={confirmBulkSend}
              data-testid="bulk-send-confirm-confirm"
              className="px-3 py-2 text-sm font-medium bg-foreground text-background rounded-md"
            >
              Send
            </button>
          </div>
        </Modal>
      )}

      {showBulkRegenModal && (
        <Modal
          testId="bulk-regenerate-modal"
          title={`Regenerate ${selectedIds.size} emails`}
          onClose={() => setShowBulkRegenModal(false)}
        >
          <textarea
            value={bulkRegenInstructions}
            onChange={(e) => setBulkRegenInstructions(e.target.value)}
            rows={3}
            placeholder="Instructions to apply to all selected emails…"
            className="mb-4 w-full rounded-md border border-border p-2 text-sm"
            data-testid="bulk-regenerate-instructions-input"
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowBulkRegenModal(false)}
              className="px-3 py-2 text-sm font-medium border border-border rounded-md hover:bg-accent"
            >
              Cancel
            </button>
            <button
              onClick={applyBulkRegenerate}
              data-testid="bulk-regenerate-confirm-button"
              className="px-3 py-2 text-sm font-medium bg-foreground text-background rounded-md"
            >
              Regenerate
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
